import { useId, useState, type ReactNode } from 'react';
import { useDsTranslate } from '../context/DsProvider';
import styles from './ReviewerChrome.module.scss';

/* ── Icons. Inline here rather than in an asset file, matching `AuditTrailView` — a ds-shared
      component carries no app asset dependency. ─────────────────────────────────────────── */

function BackIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function HowToIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" strokeLinecap="round" />
      <circle cx="12" cy="7.8" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

function ActivityIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 7v5l3 2" />
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

type Rail = 'how' | 'activity' | null;

interface ReviewerChromeProps {
  /** The contract's name. */
  title: string;
  /** Who the viewer is reviewing as. Omitted for a non-reviewer looking at the preview. */
  reviewerName?: string;
  /** Left-hand control. Omitted where there is nowhere to go back to (an emailed token). */
  onBack?: () => void;
  backLabel?: string;
  /**
   * The line under the header: who and what this contract is, and who owns its wording.
   * The prototype puts it there so a reviewer never has to ask what they are looking at
   * (`ReviewerDocumentPage.jsx:340-346`).
   */
  contextLine?: ReactNode;
  /** The right-hand action buttons. */
  actions?: ReactNode;
  /**
   * Who has finished reviewing, when this viewer has. Renders the prototype's green pill in
   * place of the actions (`ReviewerDocumentPage.jsx:321-328`) rather than leaving the header
   * empty: "I already did this" is the question a reviewer who comes back asks first, and the
   * context strip answering it in grey prose is easy to read past.
   *
   * The portal's reviewer never sees it — a finished reviewer lands on `ReviewerEndedScreen`
   * there — so today this is the external screen's state.
   */
  reviewedBy?: string;
  /** Numbered steps for the "How to review" rail. */
  howToSteps: string[];
  /** A sentence above the steps. */
  howToIntro?: ReactNode;
  /** A card under the steps — "Where this review stands". */
  howToFooter?: ReactNode;
  /** The activity rail's body. */
  activityPanel: ReactNode;
  /**
   * Which rail is open on first paint. 'how' by default, as the prototype has it
   * (`ReviewerDocumentPage.jsx:138`): a reviewer arriving from an emailed link has never seen
   * this screen, and what the activity rail would show them first is other people's history.
   * Nothing is hidden by the choice — the context strip's "N items need your action" pill
   * opens the activity rail in one click.
   */
  initialRail?: Rail;
  /** The document. */
  children: ReactNode;
}

/**
 * The chrome both reviewer screens wear: header, context strip, document, and the two rails
 * that slide beside it.
 *
 * One component rather than two implementations, because "both look and behave the same" is
 * the requirement — and they did not. The client-portal screen had no instructions at all and
 * a permanently docked activity rail; the external screen had a single dense paragraph of
 * instructions above the document and the same immovable rail. Neither could be closed to give
 * the contract the width, and neither said how many items were waiting on the reader.
 *
 * Prototype: `ReviewerDocumentPage.jsx:267-500`.
 */
export function ReviewerChrome({
  title,
  reviewerName,
  onBack,
  backLabel,
  contextLine,
  actions,
  reviewedBy,
  howToSteps,
  howToIntro,
  howToFooter,
  activityPanel,
  initialRail = 'how',
  children,
}: ReviewerChromeProps) {
  const t = useDsTranslate();
  const [rail, setRail] = useState<Rail>(initialRail);
  const railId = useId();

  const howToLabel = t('K_DS_REVIEWER_HOW_TO_REVIEW', 'How to review');
  const activityLabel = t('K_DS_REVIEWER_VIEW_ACTIVITY', 'View activity');
  const toggle = (which: Exclude<Rail, null>) => setRail((r) => (r === which ? null : which));

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          {onBack && (
            <button type="button" className={styles.backBtn} onClick={onBack}>
              <BackIcon />
              {backLabel ?? t('K_BACK', 'Back')}
            </button>
          )}
          <h1 className={styles.title} title={title}>
            {title}
          </h1>
          {reviewerName && (
            <span className={styles.reviewingAs}>
              {t('K_REVIEWER_REVIEWING_AS', 'Reviewing as {name}')
                .split('{name}')
                .flatMap((part, i) =>
                  i === 0 ? [part] : [<b key="n">{reviewerName}</b>, part],
                )}
            </span>
          )}
        </div>

        <div className={styles.headerActions}>
          <button
            type="button"
            className={`${styles.railToggle} ${rail === 'how' ? styles.railToggleOn : ''}`}
            aria-expanded={rail === 'how'}
            aria-controls={railId}
            onClick={() => toggle('how')}
          >
            <HowToIcon />
            {howToLabel}
          </button>
          <button
            type="button"
            className={`${styles.railToggle} ${rail === 'activity' ? styles.railToggleOn : ''}`}
            aria-expanded={rail === 'activity'}
            aria-controls={railId}
            onClick={() => toggle('activity')}
          >
            <ActivityIcon />
            {activityLabel}
          </button>
          {reviewedBy && (
            <>
              <span className={styles.divider} aria-hidden="true" />
              <span className={styles.reviewedPill}>
                <CheckIcon />
                {t('K_DS_REVIEWER_REVIEWED_BY', 'Reviewed by {name}').replace(
                  '{name}',
                  reviewedBy,
                )}
              </span>
            </>
          )}
          {actions && (
            <>
              <span className={styles.divider} aria-hidden="true" />
              {actions}
            </>
          )}
        </div>
      </header>

      {/* The "{n} items need your action" button is gone with the panel section it opened
          (2026-11-02, demo feedback). It promised a destination that no longer exists: the
          rail it scrolled to has no such section and nothing in the chronological trail marks
          which entries were counted, so a reviewer was told two items needed them and handed
          a list of eleven. Dropping the promise is the honest half of removing the section;
          the entries themselves are in the trail, in order. */}
      {contextLine && (
        <div className={styles.contextStrip}>
          <p className={styles.contextLine}>{contextLine}</p>
        </div>
      )}

      <div className={`${styles.body} ${rail ? styles.bodyWithRail : ''}`}>
        <div className={styles.documentPane}>{children}</div>

        {rail && (
          <aside className={styles.rail} id={railId}>
            <div className={styles.railHead}>
              <h2 className={styles.railTitle}>
                {rail === 'how' ? howToLabel : activityLabel}
              </h2>
              <button
                type="button"
                className={styles.railClose}
                aria-label={t('K_DS_REVIEWER_CLOSE_PANEL', 'Close the panel')}
                onClick={() => setRail(null)}
              >
                <CloseIcon />
              </button>
            </div>

            {/* The activity panel scrolls and pins its own composer, so it is handed the whole
                column. The how-to rail has no such machinery and takes the padded scroller. */}
            {rail === 'activity' ? (
              <div className={styles.railPanel}>{activityPanel}</div>
            ) : (
              <div className={styles.railScroll}>
                {howToIntro && <p className={styles.howToIntro}>{howToIntro}</p>}
                <ol className={styles.howToList}>
                  {howToSteps.map((step, i) => (
                    <li key={step} className={styles.howToStep}>
                      <span className={styles.howToNumber} aria-hidden="true">
                        {i + 1}
                      </span>
                      <span className={styles.howToText}>{step}</span>
                    </li>
                  ))}
                </ol>
                {howToFooter && <div className={styles.howToFooter}>{howToFooter}</div>}
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}

export default ReviewerChrome;
